/**
 * Katalog sayfasi — /katalog/:kategori
 *
 * ── KESFET'IN FILTRESI DEGIL, KULLANICIYA TANIMLI AYRI SAYFA ─────────────────
 * "Leds Play'e giren kullanici Desktop Launcher kurulu cihazlarini gorur, yeni cihaz
 * ekler, hangi ekranda hangi oyunun oynadigini izler, ekrana ozel isler ve
 * eventler kurar" (kullanici, 19 Agu 2026). Bu sayfa o operasyon merkezi:
 * kesif chip'leri YOK, magaza cercevesi YOK — sadece bu katalogun isi.
 *
 * Cihaz/oturum verisi mevcut operasyon API'lerinden gelir; derin yonetim
 * (token, duzenleme, takvim) Operasyon sayfalarinda kalir.
 */

import { Link, Navigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useMe } from '@/lib/auth';
import { ProductCard } from './Home';
import './Library.css';
import {
  Badge,
  Card,
  EmptyState,
  ErrorState,
  LoadingBlock,
  StatusDot,
} from '@/components/ui';
import {
  canonicalCatalogName,
  catalogNameLanguage,
  ENDPOINT_KIND_LABEL,
  formatDateTime,
  formatDuration,
  formatRelative,
  sessionTone,
  SESSION_STATUS_LABEL,
  slugifyTr,
} from '@/lib/format';
import { catalogBanner } from '@/lib/gameCatalog';
import { copy, localizeCatalogName, useI18n } from '@/lib/i18n';
import type { CatalogItem, Endpoint, SessionItem } from '@shared/index';

export default function CategoryPage() {
  const { kategori } = useParams();
  const me = useMe();
  const { language } = useI18n();
  const c = (tr: string, en: string) => copy(language, tr, en);

  /* Slug'dan gercek kategori adina: kategoriler sunucudan gelir, ad URL'de
     bozulmadan tasinamaz (bosluk/Turkce). Es yoksa katalog yok demektir. */
  const categories = useQuery({
    queryKey: ['catalog', 'categories'],
    queryFn: () => api.get<{ categories: string[] }>('/catalog/categories'),
    staleTime: 5 * 60_000,
  });
  const name = (categories.data?.categories ?? []).find((c) => slugifyTr(c) === kategori);
  const hasCatalogAccess = me.entitlements.some(
    (entitlement) => slugifyTr(entitlement.category) === kategori,
  );

  /* Sayfa kullaniciya TANIMLI katalogu anlatir: urunler ownedOnly. */
  const products = useQuery({
    queryKey: ['catalog', { ownedOnly: true, category: name }],
    queryFn: () =>
      api.get<{ items: CatalogItem[] }>('/catalog', { ownedOnly: true, category: name! }),
    enabled: Boolean(name && hasCatalogAccess),
  });

  /* Cihaz/yayin bolumleri ROL ile acilir, nav ile degil: "operasyon" menusu
     ilk cihaz eklenince aciliyor — nav'a bakmak ilk cihazin eklenecegi yeri
     de gizliyordu (tavuk-yumurta; saha raporu, 19 Agu 2026). */
  const canOps =
    me.user.role === 'owner' ||
    me.user.role === 'customer_admin' ||
    me.nav.includes('operations');

  const endpoints = useQuery({
    queryKey: ['endpoints'],
    queryFn: () => api.get<{ items: Endpoint[] }>('/endpoints'),
    enabled: canOps && hasCatalogAccess,
  });

  const running = useQuery({
    queryKey: ['sessions', 'running'],
    queryFn: () =>
      api.get<{ items: SessionItem[] }>('/sessions', { status: 'running', pageSize: 20 }),
    enabled: canOps && hasCatalogAccess,
  });

  const upcoming = useQuery({
    queryKey: ['sessions', 'upcoming'],
    queryFn: () =>
      api.get<{ items: SessionItem[] }>('/sessions', {
        from: new Date().toISOString(),
        status: 'scheduled',
        pageSize: 5,
      }),
    enabled: canOps && hasCatalogAccess,
  });

  if (categories.isLoading) return <LoadingBlock rows={4} label={c('Katalog yükleniyor', 'Loading catalog')} />;
  if (categories.isError)
    return <ErrorState message={c('Katalog alınamadı.', 'Catalog could not be loaded.')} onRetry={() => void categories.refetch()} />;

  // Menü gizlemek güvenlik/akış kapısı değildir. Eski yer imi veya elle
  // yazılmış URL de sonlandırılmış katalog çalışma alanını açmamalıdır.
  if (name && !hasCatalogAccess) return <Navigate to="/" replace />;

  if (!name) {
    return (
      <Card>
        <EmptyState
          title={c('Katalog bulunamadı', 'Catalog not found')}
          description={c('Bu adreste bir katalog yok ya da hesabınıza tanımlı değil.', 'There is no catalog at this address, or it is not assigned to your account.')}
          action={
            <Link className="btn btn--primary btn--md" to="/kesfet">{c("Keşfet'e dön", 'Return to Discover')}</Link>
          }
        />
      </Card>
    );
  }

  const items = products.data?.items ?? [];
  const displayName = canonicalCatalogName(name);
  const localizedName = localizeCatalogName(displayName, language);
  const productIds = new Set(items.map((i) => i.id));
  const flow = [...(running.data?.items ?? []), ...(upcoming.data?.items ?? [])].filter(
    (s) => s.productId !== null && productIds.has(s.productId),
  );
  const eps = endpoints.data?.items ?? [];
  const heroImage = items[0] ? catalogBanner(items[0]) : categoryFallbackImage(displayName);
  const journey = categoryJourney(displayName, language);

  return (
    <div className="category-workspace stack stack--lg">
      <header className="category-workspace__head">
        <div className="category-workspace__crumbs"><Link to="/kesfet">{c('Keşfet', 'Discover')}</Link><span>/</span><span lang={catalogNameLanguage(displayName)}>{localizedName}</span></div>
        <h1 lang={catalogNameLanguage(displayName)}>{localizedName}</h1>
        <p>{c('Bu katalogdaki ürünlerinizi, cihazlarınızı ve oturum akışınızı tek yerde yönetin.', 'Manage your products, devices and session flow for this catalog in one place.')}</p>
      </header>

      <section className="category-workspace__hero" aria-label={`${localizedName} ${c('kataloğu', 'catalog')}`}>
        {heroImage ? <img src={heroImage} alt="" /> : null}
        <div><h2>{journey.title}</h2><p>{journey.description}</p><Link className="btn btn--inverse btn--md" to={`/kesfet?kategori=${encodeURIComponent(name)}`}>{c('Tüm seçkiyi incele', 'Browse the full collection')}</Link></div>
      </section>

      <ol className="category-workspace__journey">
        {journey.steps.map((step, index) => <li key={step.title}><span className="num">{index + 1}</span><div><strong>{step.title}</strong><p>{step.text}</p></div></li>)}
      </ol>

      <section className="category-workspace__products">
        <div className="row row--between">
          <div><h2>{c('Tanımlı ürünleriniz', 'Assigned products')}</h2><p className="muted">{c('Satın alınan ve CORTEX kitaplığınıza bağlanan içerikler.', 'Purchased content linked to your CORTEX library.')}</p></div>
          <Link to={`/kesfet?kategori=${encodeURIComponent(name)}`} className="btn btn--secondary btn--sm">{c('Kataloğun tamamı', 'Full catalog')}</Link>
        </div>
        {products.isLoading ? <LoadingBlock rows={3} /> : items.length === 0 ? <Card><EmptyState title={c('Bu katalogdan tanımlı ürününüz yok', 'No products from this catalog are assigned yet')} description={c('Katalogdaki çözümleri inceleyip kurumunuz için teklif alabilirsiniz.', 'Browse the catalog solutions and request a quote for your organization.')} action={<Link className="btn btn--primary btn--md" to={`/kesfet?kategori=${encodeURIComponent(name)}`}>{c('Kataloğa bak', 'View catalog')}</Link>} /></Card> : <div className="grid-covers">{items.map((item) => <ProductCard key={item.id} item={item} />)}</div>}
      </section>

      {canOps && (
        <div className="catws">
          <Card
            title={c('Ekranlar ve cihazlar', 'Screens and devices')}
            action={
              <div className="row" style={{ gap: 'var(--space-2)' }}>
                <Badge>
                  {eps.length}/{me.account.deviceLimit} {c('cihaz', 'devices')}
                </Badge>
                <Link className="btn btn--primary btn--sm" to="/operasyon/ekranlar">{c('Cihaz ekle / yönet →', 'Add / manage devices →')}</Link>
              </div>
            }
          >
            {endpoints.isLoading ? (
              <LoadingBlock rows={2} />
            ) : endpoints.isError ? (
              <ErrorState
                message={c('Cihaz listesi alınamadı.', 'Device list could not be loaded.')}
                onRetry={() => void endpoints.refetch()}
              />
            ) : eps.length === 0 ? (
              <EmptyState
                title={c('Kayıtlı cihaz yok', 'No registered devices')}
                description={c('CORTEX kurulu bilgisayarınızı bağlayın; ekranları ve canlı durumu burada görünür.', 'Connect a computer with CORTEX installed; its screens and live status will appear here.')}
                action={
                  <Link className="btn btn--primary btn--md" to="/operasyon/ekranlar">{c('İlk cihazınızı ekleyin', 'Add your first device')}</Link>
                }
              />
            ) : (
              <ul className="catws__eplist">
                {eps.map((ep) => (
                  <li key={ep.id} className="catws__ep">
                    <div className="catws__epname">
                      <strong>{ep.name}</strong>
                      <Badge>{ENDPOINT_KIND_LABEL[ep.kind] ?? ep.kind}</Badge>
                      {ep.location && <span className="small muted">{ep.location}</span>}
                    </div>
                    <StatusDot
                      tone={
                        ep.status === 'playing'
                          ? 'primary'
                          : ep.status === 'online'
                            ? 'success'
                            : 'danger'
                      }
                      label={
                        ep.status === 'playing'
                          ? `${c('Oynatıyor', 'Playing')}: ${ep.nowPlaying?.productName ?? ''}`
                          : ep.status === 'online'
                            ? c('Çevrimiçi', 'Online')
                            : `${c('Çevrimdışı', 'Offline')} · ${formatRelative(ep.lastSeenAt)}`
                      }
                    />
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card
            title={c('Yayın akışı', 'Session flow')}
            action={
              <Link className="btn btn--ghost btn--sm" to="/operasyon/takvim?planla=1">{c('Oturum planla →', 'Schedule session →')}</Link>
            }
          >
            {running.isLoading || upcoming.isLoading ? (
              <LoadingBlock rows={2} />
            ) : flow.length === 0 ? (
              <EmptyState
                title={c('Şu an yayın yok', 'No active session')}
                description={c('Bu katalogdan çalışan veya planlı bir oturum olduğunda burada görünür.', 'Active or scheduled sessions from this catalog will appear here.')}
                action={
                  <Link className="btn btn--primary btn--md" to="/operasyon/takvim?planla=1">{c('Oturum planla', 'Schedule session')}</Link>
                }
              />
            ) : (
              <ul className="catws__flow">
                {flow.map((s) => (
                  <li key={s.id} className="catws__session">
                    <div>
                      <strong>{s.productName}</strong>
                      <div className="small muted">
                        {s.endpointName} · {formatDateTime(s.startsAt)} ·{' '}
                        {formatDuration(s.durationMinutes)}
                      </div>
                    </div>
                    <Badge tone={sessionTone(s.status)}>{language === 'tr' ? SESSION_STATUS_LABEL[s.status] : sessionStatusEnglish(s.status)}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      )}

    </div>
  );
}

function categoryFallbackImage(category: string): string {
  if (category === 'Leds Play') return '/media/games-v2/carpisan-araba-banner.webp';
  if (category === 'VR Deneyimleri') return '/media/vr-v2/vr-arena.webp';
  if (category === 'AR Deneyimleri') return '/media/ar-v2/dinozor-parki-ar.webp';
  return '/media/immersive-v2/gelgit-odasi.webp';
}

function categoryJourney(category: string, language: 'tr' | 'en') {
  const c = (tr: string, en: string) => copy(language, tr, en);
  if (category === 'Leds Play') return { title: c('Şehir ekranını ortak bir oyun alanına dönüştürün.', 'Turn a city screen into a shared play space.'), description: c('Katılımcı telefonu kumanda olarak kullanır; oturum ve cihaz akışı Hub üzerinden yönetilir.', 'Participants use their phones as controllers while Hub manages sessions and devices.'), steps: [{ title: c('QR okutulur', 'Scan the QR code'), text: c('Katılımcı indirme yapmadan oyuna katılır.', 'Participants join without installing an app.') }, { title: c('Telefon kumanda olur', 'The phone becomes a controller'), text: c('Tarayıcıdaki kontroller ekrana bağlanır.', 'Browser controls connect to the screen.') }, { title: c('Oyun ekranda başlar', 'The game starts on screen'), text: c('Operatör oturumu ve cihazı yönetir.', 'The operator manages the session and device.') }] };
  if (category === 'VR Deneyimleri') return { title: c('VR istasyonunuzu lisanslı içeriklerle işletin.', 'Operate your VR station with licensed experiences.'), description: c('Satın alınan deneyimler CORTEX kitaplığına gelir ve uygun bilgisayarda çalıştırılır.', 'Purchased experiences arrive in the CORTEX library and run on a compatible computer.'), steps: [{ title: c('İçerik atanır', 'Content is assigned'), text: c('Lisans kuruma ve uygun cihaza bağlanır.', 'The license is linked to the organization and a compatible device.') }, { title: c('CORTEX hazırlar', 'CORTEX prepares it'), text: c('Gerekli bileşenler arka planda indirilir.', 'Required components download in the background.') }, { title: c('Operatör başlatır', 'The operator launches it'), text: c('Deneyim istasyonda tek noktadan açılır.', 'The experience launches from a single station interface.') }] };
  if (category === 'AR Deneyimleri') return { title: c('Fiziksel mekâna dijital bir keşif katmanı ekleyin.', 'Add a digital discovery layer to a physical venue.'), description: c('Park, fuar ve kültür alanları kuruma özel AR rotaları ve çok oyunculu deneyimlerle dönüşür.', 'Parks, fairs and cultural venues become organization-specific AR routes and multiplayer experiences.'), steps: [{ title: c('Mekân tanımlanır', 'Define the venue'), text: c('Rota, duraklar ve kullanım senaryosu belirlenir.', 'Define the route, stops and usage scenario.') }, { title: c('Proje hazırlanır', 'Prepare the project'), text: c('İçerik fiziksel alanla eşleştirilir.', 'Match the content to the physical space.') }, { title: c('Ziyaretçi keşfeder', 'Visitors explore'), text: c('Deneyim kurumun alanında kontrollü biçimde açılır.', 'Launch the experience in a controlled way at the organization’s venue.') }] };
  return { title: c('Hareketi mekânın tamamına yayılan bir esere dönüştürün.', 'Turn movement into an artwork that fills the entire venue.'), description: c('Motion Track, projeksiyon ve gerçek zamanlı görsel sistemler aynı operasyon akışında buluşur.', 'Motion Track, projection and real-time visual systems meet in one operation flow.'), steps: [{ title: c('Sahne profili kurulur', 'Set up the stage profile'), text: c('Mekân, sensör ve gösterim yüzeyleri eşleştirilir.', 'Match the venue, sensors and display surfaces.') }, { title: c('Hareket izlenir', 'Track movement'), text: c('Performans verisi gerçek zamanlı görsele dönüşür.', 'Performance data becomes real-time visuals.') }, { title: c('Eser çalıştırılır', 'Run the artwork'), text: c('Operatör lisanslı içeriği CORTEX üzerinden başlatır.', 'The operator launches licensed content through CORTEX.') }] };
}

function sessionStatusEnglish(status: string): string {
  return ({ scheduled: 'Scheduled', running: 'Running', completed: 'Completed', stopped: 'Stopped', failed: 'Failed' } as Record<string, string>)[status] ?? status;
}
